export interface GenerateParams {
  board: string;
  grade: string;
  subject: string;
  topic: string;
  item_type: string;
  bloom_level: string;
  question_count: number;
  learning_outcome?: string;
}

export interface OptionExplanation {
  option: string;
  is_correct: boolean;
  explanation: string;
}

export type QuestionImagePersistenceParent = 'generation_history' | 'assessment_bundle';

export interface QuestionImagePersistenceTarget {
  parentType: QuestionImagePersistenceParent;
  parentId: string;
  questionIndex: number;
  itemId?: string | number;
}

export interface QuestionImageMetadata {
  provider?: string;
  model_used?: string;
  generated_at?: string;
  saved_at?: string;
  prompt?: string;
  prompt_used?: string;
  original_description?: string;
  mime_type?: string;
  byte_size?: number;
  storage_bucket?: string;
  storage_path?: string;
  public_url?: string;
  parent_type?: QuestionImagePersistenceParent;
  parent_id?: string;
  question_index?: number;
  item_id?: string | number;
  [key: string]: unknown;
}

export interface QuestionImageUpdate {
  image_prompt?: string | null;
  generated_image_svg?: string | null;
  accepted_image_svg?: string | null;
  generated_image_data_url?: string | null;
  accepted_image_data_url?: string | null;
  generated_image_url?: string | null;
  accepted_image_url?: string | null;
  generated_image_storage_path?: string | null;
  accepted_image_storage_path?: string | null;
  generated_image_mime_type?: string | null;
  accepted_image_mime_type?: string | null;
  generated_image_prompt?: string | null;
  accepted_image_prompt?: string | null;
  generated_image_metadata?: QuestionImageMetadata | null;
  accepted_image_metadata?: QuestionImageMetadata | null;
}

export interface Question {
  id: number;
  question: string;
  type: string;
  options: string[] | null;
  correct_answer: string | string[];
  explanation: string;
  option_explanations?: OptionExplanation[];
  bloom_level: string;
  difficulty: string;
  confidence?: number;
  source_basis?: 'topic_content' | 'retrieved_web' | 'learning_outcome' | 'general_curriculum';
  image_description?: string | null;
  image_prompt?: string | null;
  generated_image_svg?: string | null;
  accepted_image_svg?: string | null;
  generated_image_data_url?: string | null;
  accepted_image_data_url?: string | null;
  generated_image_url?: string | null;
  accepted_image_url?: string | null;
  generated_image_storage_path?: string | null;
  accepted_image_storage_path?: string | null;
  generated_image_mime_type?: string | null;
  accepted_image_mime_type?: string | null;
  generated_image_prompt?: string | null;
  accepted_image_prompt?: string | null;
  generated_image_metadata?: QuestionImageMetadata | null;
  accepted_image_metadata?: QuestionImageMetadata | null;
  column_a?: string[];
  column_b?: string[];
}

export interface GenerationHistoryItem {
  id: string;
  user_id: string;
  board: string;
  grade: number;
  subject: string;
  topic: string;
  item_type: string;
  bloom_level: string;
  question_count: number;
  include_images: boolean;
  learning_outcome: string;
  questions: Question[];
  metadata: GenerateResponse['metadata'];
  created_at: string;
}

export interface GenerateResponse {
  success: boolean;
  questions: Question[];
  metadata: {
    board: string;
    grade: string;
    subject: string;
    topic: string;
    item_type: string;
    bloom_level: string;
    count: number;
    history_id?: string;
    ai_powered: boolean;
    model_used?: string;
    source_basis?: 'topic_content' | 'retrieved_web' | 'learning_outcome' | 'general_curriculum';
    generated_at: string;
    content_discovery?: {
      status: 'provided_content' | 'retrieved_web' | 'foundational_fallback';
      message: string;
      warning?: string;
      sourceCount?: number;
      validatedSourceCount?: number;
      queries?: string[];
    };
    quality_context?: {
      adminPanelQualityInputsGlobal?: boolean;
      aiRulesLookedUp?: boolean;
      aiRulesUsed: boolean;
      bloomSamplesLookedUp?: boolean;
      bloomSamplesUsed: boolean;
      itemTypeSamplesLookedUp?: boolean;
      itemTypeSamplesUsed?: boolean;
      curriculumChaptersUsed?: boolean;
      curriculumChapterMatched?: boolean;
      topicContentUsed: boolean;
      retrievedWebUsed?: boolean;
      feedbackUsed: boolean;
    };
  };
}

export interface Upload {
  id: string;
  user_id: string;
  file_name: string;
  file_url: string;
  file_type: string;
  board: string;
  grade: string;
  subject: string;
  topic: string;
  processed_text: string;
  status: string;
  created_at: string;
}

export interface BundleQuestion extends Question {
  points: number;
}

export interface AssessmentBundle {
  id: string;
  user_id: string;
  name: string;
  description: string;
  time_limit_minutes: number;
  total_points: number;
  questions: BundleQuestion[];
  created_at: string;
  updated_at: string;
}

export const itemTypes = [
  'Multiple Choice (Single Correct)',
  'Multiple Select',
  'Fill in the Blanks',
  'Inline Choice',
  'Matching',
  'True/False',
] as const;

export const itemTypeDescriptions: Record<string, string> = {
  'Multiple Choice (Single Correct)': 'One correct answer from multiple options',
  'Multiple Select': 'Multiple correct answers from given options',
  'Fill in the Blanks': 'Student inputs the missing term',
  'Inline Choice': 'Select the correct word from options within a sentence',
  'Matching': 'Match items from Column A with items in Column B',
  'True/False': 'Evaluate whether a statement is true or false',
};

export const bloomLevels = [
  'Remembering',
  'Understanding',
  'Applying',
  'Analyzing',
  'Evaluating',
  'Creating',
] as const;

export interface AiRule {
  id: string;
  category: string;
  subcategory: string;
  rules: string;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface BloomSampleItem {
  id: string;
  bloom_level: string;
  sample_question: string;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ItemTypeSampleItem {
  id: string;
  item_type: string;
  sample_question: string;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface TopicContent {
  id: string;
  topic_name: string;
  subject: string;
  grade: number;
  title: string;
  content: string;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CurriculumChapter {
  id: string;
  grade: number;
  subject: string;
  chapter_name: string;
  sort_order: number;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export const bloomPricing: Record<string, number> = {
  Remembering: 5,
  Understanding: 7,
  Applying: 9,
  Analyzing: 12,
  Evaluating: 15,
  Creating: 20,
};

export const SIGNUP_BONUS_AMOUNT = 50;
export const IMAGE_GENERATION_PRICE_INR = 7;

export interface WalletTransaction {
  id: string;
  user_id: string;
  type: 'credit' | 'debit' | 'refund' | 'free_use';
  amount: number;
  free_questions_used: number;
  generation_id: string | null;
  description: string;
  razorpay_order_id: string | null;
  razorpay_payment_id: string | null;
  status: 'pending' | 'success' | 'failed';
  created_at: string;
}

export const aiRuleCategories = [
  { value: 'question_type', label: 'Question Type' },
  { value: 'bloom_level', label: "Bloom's Level" },
  { value: 'subject', label: 'Subject' },
  { value: 'grade', label: 'Grade' },
] as const;
